import React, { useMemo, useEffect, useState, Fragment } from 'react';
import { CheckIcon, XMarkIcon, UserPlusIcon, PlusIcon } from '@heroicons/react/24/solid';
import { ClockIcon, UserCircleIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { updateCompanyStatusHistory, markCompanyContacted } from '../../../services/firebase';
import ContactSelector from '../../features/dashboard/jobs/ContactSelector';
import FastTrackContactModal from '../../features/dashboard/jobs/FastTrackContactModal';
import { getAuth } from 'firebase/auth';

export default function CandidateProgressTable({ candidates, loading, onCandidateClick, selectedIds, onSelectIds }) {
  const [companyCache, setCompanyCache] = useState({});
  const [adminCache, setAdminCache] = useState({});
  const [showContactModal, setShowContactModal] = useState(false);
  const [showTagLeadModal, setShowTagLeadModal] = useState(false);
  const [showOutreachEmailModal, setShowOutreachEmailModal] = useState(false);
  const [selectedCompanyForContact, setSelectedCompanyForContact] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedContactLead, setSelectedContactLead] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactNotes, setContactNotes] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const db = getFirestore();

  // Admin options for the dropdown
  const adminOptions = [
    { id: 'grey@justburnt.com', name: 'Grey', uid: 'grey123' },
    { id: 'arjan@justburnt.com', name: 'Arjan', uid: 'arjan123' },
    { id: 'louie@justburnt.com', name: 'Louie', uid: 'louie123' }
  ];

  // Fetch company details when needed
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      // Get all unique company IDs from all candidates
      const companyIds = new Set();
      
      candidates.forEach(candidate => {
        if (candidate.companyStatusHistory) {
          Object.keys(candidate.companyStatusHistory).forEach(companyId => {
            if (!companyCache[companyId]) {
              companyIds.add(companyId);
            }
          });
        }
      });
      
      // Convert to array and remove any already cached
      const companyIdsToFetch = [...companyIds];
      
      // Don't proceed if nothing to fetch
      if (companyIdsToFetch.length === 0) return;
      
      // Fetch company details for each ID
      const newCompanyDetails = { ...companyCache };
      
      await Promise.all(
        companyIdsToFetch.map(async (companyId) => {
          try {
            const companyRef = doc(db, 'companies', companyId);
            const companySnap = await getDoc(companyRef);
            
            if (companySnap.exists()) {
              newCompanyDetails[companyId] = companySnap.data();
            }
          } catch (error) {
            console.error(`Error fetching company ${companyId}:`, error);
          }
        })
      );
      
      setCompanyCache(newCompanyDetails);
    };
    
    fetchCompanyDetails();
  }, [candidates]);

  // Fetch admin details - update to handle permissions errors gracefully
  useEffect(() => {
    const fetchAdminDetails = async () => {
      // Get all unique admin UIDs from all candidates
      const adminIds = new Set();
      
      candidates.forEach(candidate => {
        if (candidate.companyStatusHistory) {
          Object.entries(candidate.companyStatusHistory).forEach(([_, events]) => {
            if (Array.isArray(events)) {
              events.forEach(event => {
                // Check for admin UID in status events
                if (event.adminUid && !adminCache[event.adminUid]) {
                  adminIds.add(event.adminUid);
                }
              });
            }
          });
        }
      });
      
      // Don't proceed if nothing to fetch
      if (adminIds.size === 0) return;
      
      // Fetch admin details for each ID
      const newAdminDetails = { ...adminCache };
      
      // Map of hardcoded admin data to use as fallback
      const hardcodedAdmins = {
        'grey123': { firstName: 'Grey', email: 'grey@justburnt.com' },
        'arjan123': { firstName: 'Arjan', email: 'arjan@justburnt.com' },
        'louie123': { firstName: 'Louie', email: 'louie@justburnt.com' }
      };
      
      await Promise.all(
        [...adminIds].map(async (adminId) => {
          try {
            // Try to get from Firestore first
            const adminRef = doc(db, 'admins', adminId);
            const adminSnap = await getDoc(adminRef);
            
            if (adminSnap.exists()) {
              newAdminDetails[adminId] = adminSnap.data();
            } else {
              // Try users collection if not found in admins
              try {
                const userRef = doc(db, 'users', adminId);
                const userSnap = await getDoc(userRef);
                
                if (userSnap.exists()) {
                  newAdminDetails[adminId] = userSnap.data();
                } else if (hardcodedAdmins[adminId]) {
                  // Use hardcoded data as fallback
                  newAdminDetails[adminId] = hardcodedAdmins[adminId];
                }
              } catch (userError) {
                // If any error accessing users collection, use hardcoded data
                if (hardcodedAdmins[adminId]) {
                  newAdminDetails[adminId] = hardcodedAdmins[adminId];
                }
                console.log(`Using hardcoded data for admin ${adminId}`);
              }
            }
          } catch (error) {
            console.log(`Using hardcoded data for admin ${adminId}`);
            // Use hardcoded data on error
            if (hardcodedAdmins[adminId]) {
              newAdminDetails[adminId] = hardcodedAdmins[adminId];
            } else {
              // For non-hardcoded admins, create a placeholder
              const adminEmail = adminOptions.find(a => a.uid === adminId)?.id;
              const adminName = adminOptions.find(a => a.uid === adminId)?.name;
              
              if (adminEmail || adminName) {
                newAdminDetails[adminId] = { 
                  firstName: adminName || 'Admin',
                  email: adminEmail
                };
              }
            }
          }
        })
      );
      
      setAdminCache(newAdminDetails);
    };
    
    fetchAdminDetails();
  }, [candidates]);

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Check if timestamp is a Firestore timestamp
      if (timestamp.toDate) {
        return format(timestamp.toDate(), 'MMM d, yyyy, h:mm a');
      }
      
      // Check if timestamp is a string (ISO format)
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), 'MMM d, yyyy, h:mm a');
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };
  
  // Helper function to format date only (no time)
  const formatDateOnly = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Check if timestamp is a Firestore timestamp
      if (timestamp.toDate) {
        return format(timestamp.toDate(), 'MMM d, yyyy');
      }
      
      // Check if timestamp is a string (ISO format)
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), 'MMM d, yyyy');
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Updated function to get ALL companies from status history with proper name lookup
  const getAllCompanyInfo = (candidate) => {
    if (!candidate || !candidate.companyStatusHistory) return [];
    
    const companies = [];
    
    // Fallback company names (in case fetch fails)
    const fallbackNames = {
      'j7SQclOwgwAfplE3dnid': 'Netflix',
      'Ml6gX4KdIxA4hTeQUif3': 'Google',
      'DukaHGtNUrHHspUPzV7q': 'Amazon',
      'C8m7EtHzWffBFRwwfIsM': 'Microsoft',
      'pKcfy4NUjGPx9r1KzKDo': 'Apple',
      'aO07pEIwVNmOdPdaoKrs': 'IBM',
      'ai17XaACAb5t6y8wKWWr': 'Twilio'
    };
    
    // Process each company in the history
    Object.entries(candidate.companyStatusHistory || {}).forEach(([companyId, events]) => {
      if (!Array.isArray(events) || events.length === 0) return;
      
      // Find the most recent event for this company
      const sortedEvents = [...events].sort((a, b) => {
        const timeA = a.timestamp?.toDate ? 
          a.timestamp.toDate() : 
          new Date(a.timestamp || 0);
        const timeB = b.timestamp?.toDate ? 
          b.timestamp.toDate() : 
          new Date(b.timestamp || 0);
        return timeB - timeA; // Most recent first
      });
      
      // Find the introduction request event
      const introEvent = events.find(e => e.status === 'introduction-requested');
      
      if (!introEvent) return; // Skip if no intro request
      
      // Check if the connection has been undone (most recent event has null status)
      if (sortedEvents[0].status === null) return; // Skip if connection was undone
      
      // Get company details from our cache
      const companyDetails = companyCache[companyId];
      
      // Get the company name from various sources
      const name = 
        (companyDetails?.name) || 
        fallbackNames[companyId] || 
        `Company ${companyId.substring(0, 8)}...`;
      
      companies.push({
        id: companyId,
        name: name,
        logo: companyDetails?.logo || null,
        timestamp: introEvent.timestamp,
        status: sortedEvents[0].status,
        mostRecentEvent: sortedEvents[0]
      });
    });
    
    // Sort companies by timestamp (most recent first)
    companies.sort((a, b) => {
      const timeA = a.timestamp?.toDate ? 
        a.timestamp.toDate() : 
        new Date(a.timestamp || 0);
      const timeB = b.timestamp?.toDate ? 
        b.timestamp.toDate() : 
        new Date(b.timestamp || 0);
      return timeB - timeA;
    });
    
    return companies;
  };
  
  // Enhanced function to find the contact lead for a company with additional details
  const findContactLead = (candidate, companyId) => {
    if (!candidate || !companyId || !candidate.companyStatusHistory?.[companyId]) return null;
    
    const events = candidate.companyStatusHistory[companyId];
    if (!Array.isArray(events)) return null;
    
    // Get the most recent assignment event
    const leadEvents = events
      .filter(e => e.status === 'contact_lead_assigned' || e.status === 'contact_lead_changed')
      .sort((a, b) => {
        const timeA = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
        const timeB = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
        return timeB - timeA; // Most recent first
      });
    
    if (leadEvents.length === 0) return null;
    
    const event = leadEvents[0];
    
    // Try to get admin details from cache
    const adminDetails = event.adminUid ? adminCache[event.adminUid] : null;
    
    // Set default admin info with enhanced data
    return {
      name: event.assignedAdminName || adminDetails?.firstName || adminDetails?.displayName || 'Unknown Admin',
      email: event.assignedAdminEmail || adminDetails?.email,
      uid: event.adminUid,
      photoURL: adminDetails?.photoURL || null
    };
  };
  
  // Helper to check for introduction requests
  const hasIntroRequest = (candidate) => {
    if (!candidate || !candidate.companyStatusHistory) return false;
    
    // Check if any company has an introduction request
    return Object.values(candidate.companyStatusHistory).some(events => 
      Array.isArray(events) && events.some(e => e.status === 'introduction-requested')
    );
  };
  
  // Generate a consistent color based on admin name or ID
  const getAdminColor = (adminName, adminId) => {
    // Set of visually distinct colors with good contrast for text
    const colors = [
      { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-600 dark:text-red-400' },
      { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-600 dark:text-blue-400' },
      { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-600 dark:text-green-400' },
      { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-600 dark:text-yellow-400' },
      { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-600 dark:text-purple-400' },
      { bg: 'bg-pink-100 dark:bg-pink-900', text: 'text-pink-600 dark:text-pink-400' },
      { bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-600 dark:text-indigo-400' },
      { bg: 'bg-teal-100 dark:bg-teal-900', text: 'text-teal-600 dark:text-teal-400' },
      { bg: 'bg-orange-100 dark:bg-orange-900', text: 'text-orange-600 dark:text-orange-400' },
      { bg: 'bg-cyan-100 dark:bg-cyan-900', text: 'text-cyan-600 dark:text-cyan-400' }
    ];
    
    // Convert adminName to string if it's an object
    const safeAdminName = typeof adminName === 'object' ? (adminName.text || '') : adminName;
    
    // Use a simple hash function based on the admin name or ID to select a color
    const identifier = safeAdminName || adminId || 'unknown';
    let hash = 0;
    
    for (let i = 0; i < identifier.length; i++) {
      hash = (hash + identifier.charCodeAt(i)) % colors.length;
    }
    
    // Special colors for specific admins
    if (safeAdminName === 'Grey') {
      return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400' };
    } else if (safeAdminName === 'Arjan') {
      return { bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-600 dark:text-indigo-400' };
    } else if (safeAdminName === 'Louie') {
      return { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-600 dark:text-blue-400' };
    }
    
    return colors[hash];
  };
  
  // Sort candidates - intro requests first, then by name
  const sortedCandidates = useMemo(() => {
    if (!candidates || !Array.isArray(candidates)) return [];
    
    return [...candidates].sort((a, b) => {
      const aHasRequest = hasIntroRequest(a);
      const bHasRequest = hasIntroRequest(b);
      
      if (aHasRequest && !bHasRequest) return -1;
      if (!aHasRequest && bHasRequest) return 1;
      
      // Sort by name if intro request status is the same
      const aName = a.displayName || a.email || '';
      const bName = b.displayName || b.email || '';
      return aName.localeCompare(bName);
    });
  }, [candidates]);

  // Handle individual candidate selection
  const handleCheckboxChange = (candidateId, e) => {
    e.stopPropagation(); // Prevent row click event from firing
    
    if (selectedIds.includes(candidateId)) {
      onSelectIds(selectedIds.filter(id => id !== candidateId));
    } else {
      onSelectIds([...selectedIds, candidateId]);
    }
  };
  
  // Handle select all checkbox
  const handleSelectAll = (e) => {
    e.stopPropagation();
    
    if (selectedIds.length === sortedCandidates.length) {
      // If all are selected, deselect all
      onSelectIds([]);
    } else {
      // Otherwise select all
      onSelectIds(sortedCandidates.map(candidate => candidate.id));
    }
  };
  
  // Custom checkbox component
  const CustomCheckbox = ({ isChecked, onChange }) => {
    return (
      <div 
        className="w-4 h-4 rounded border relative cursor-pointer flex items-center justify-center"
        style={{ 
          borderColor: isChecked ? '#8B5CF6' : 'rgba(156, 163, 175, 0.6)',
          backgroundColor: isChecked ? '#8B5CF6' : 'transparent' 
        }}
        onClick={onChange}
      >
        {isChecked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 1L3.5 6.5L1 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    );
  };

  // Custom checkbox component with indeterminate state
  const CustomHeaderCheckbox = ({ isChecked, isIndeterminate, onChange }) => {
    return (
      <div 
        className="w-4 h-4 rounded border relative cursor-pointer flex items-center justify-center"
        style={{ 
          borderColor: isChecked || isIndeterminate ? '#8B5CF6' : 'rgba(156, 163, 175, 0.6)',
          backgroundColor: isChecked || isIndeterminate ? '#8B5CF6' : 'transparent' 
        }}
        onClick={onChange}
      >
        {isChecked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 1L3.5 6.5L1 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {isIndeterminate && !isChecked && (
          <svg width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1H9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    );
  };

  // Component to render a single company with its icon
  const CompanyDisplay = ({ company }) => (
    <div className="flex items-center mb-2 last:mb-0">
      {company.logo ? (
        <div className="h-7 w-7 flex-shrink-0 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
          <img 
            src={company.logo} 
            alt={company.name} 
            className="h-full w-full object-cover" 
          />
        </div>
      ) : (
        <div className="h-7 w-7 flex-shrink-0 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
          <BuildingOfficeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>
      )}
      <div className="ml-3 flex flex-col">
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {company.name}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatDateOnly(company.timestamp)}
        </span>
      </div>
    </div>
  );

  // Component to render a single contact lead with its icon
  const ContactLeadDisplay = ({ contactLead }) => {
    // Make sure contactLead.name is a string
    const safeContactName = typeof contactLead.name === 'object' 
      ? (contactLead.name.text || 'Unknown') 
      : (contactLead.name || 'Unknown');

    const colorClasses = getAdminColor(safeContactName, contactLead.uid);

    return (
      <div className="flex items-center mb-2 last:mb-0">
        {contactLead.photoURL ? (
          <div className="h-7 w-7 flex-shrink-0 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
            <img 
              src={contactLead.photoURL} 
              alt={safeContactName} 
              className="h-full w-full object-cover" 
            />
          </div>
        ) : (
          <div className={`h-7 w-7 flex-shrink-0 rounded-full ${colorClasses.bg} flex items-center justify-center`}>
            <UserCircleIcon className={`h-4 w-4 ${colorClasses.text}`} />
          </div>
        )}
        <span className={`ml-3 text-sm font-medium ${contactLead.photoURL ? 'text-indigo-600 dark:text-indigo-400' : colorClasses.text}`}>
          {safeContactName}
        </span>
      </div>
    );
  };

  // Helper to check if a company has been contacted
  const isCompanyContacted = (candidate, companyId) => {
    // Check in contactOutreach
    if (candidate.contactOutreach && 
        candidate.contactOutreach[companyId] && 
        candidate.contactOutreach[companyId].status === 'contacted') {
      return true;
    }
    
    // Check in companyStatuses
    if (candidate.companyStatuses && 
        candidate.companyStatuses[companyId] === 'contact-established') {
      return true;
    }
    
    // Check in companyStatusHistory for admin_contacted_company events
    if (candidate.companyStatusHistory && 
        Array.isArray(candidate.companyStatusHistory[companyId])) {
      return candidate.companyStatusHistory[companyId].some(
        event => event.status === 'admin_contacted_company'
      );
    }
    
    return false;
  };

  // Helper function to safely get display name
  const getSafeDisplayName = (user) => {
    if (!user) return 'Unknown User';
    
    // Use the enhanced displayName from CandidateProgressView first
    if (user.displayName) {
      if (typeof user.displayName === 'object' && user.displayName.text) {
        return user.displayName.text;
      } else if (typeof user.displayName === 'string') {
        return user.displayName;
      }
    }
    
    // Fallback to basicInfo
    if (user.basicInfo?.firstName && user.basicInfo?.lastName) {
      return `${user.basicInfo.firstName} ${user.basicInfo.lastName}`;
    }
    
    if (user.basicInfo?.name) {
      return user.basicInfo.name;
    }
    
    // Try to extract from resume data as fallback
    if (user.resumeName && typeof user.resumeName === 'string') {
      const nameMatch = user.resumeName.match(/^([A-Za-z\s]+?)(?:\s+Resume|\s+CV|\.pdf|\.doc)/i);
      if (nameMatch && nameMatch[1].trim().length > 2) {
        return nameMatch[1].trim();
      }
    }
    
    // Use email as final fallback
    const email = user.basicInfo?.email || user.email;
    if (email) {
      const emailName = email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return 'Unknown User';
  };

  // Add generateEmailTemplate function to create email templates
  const generateEmailTemplate = (contact, candidate, company) => {
    if (!contact || !candidate || !company) return '';
    
    const contactName = contact.firstName || 'there';
    const contactEmail = contact.email || '';
    const candidateName = getSafeDisplayName(candidate);
    const candidateFirstName = candidateName.split(' ')[0];
    
    // Use the contact lead's name if available, otherwise default to current admin
    const adminFirstName = selectedContactLead?.name || adminOptions.find(a => a.id === getAuth().currentUser?.email)?.name || 'The Burnt Team';
    
    // Extract profile summary if available
    let profileSummary = '';
    
    if (candidate.profileSummary?.profileSummary) {
      profileSummary = candidate.profileSummary.profileSummary;
    } else if (candidate.basicInfo?.professionalSummary) {
      profileSummary = candidate.basicInfo.professionalSummary;
    } else if (candidate.basicInfo?.bio) {
      profileSummary = candidate.basicInfo.bio;
    } else {
      // Generate a basic summary if none exists
      const candidateTitle = candidate.currentPosition || candidate.basicInfo?.title || 'professional';
      profileSummary = `${candidateName} is a talented ${candidateTitle} looking for new opportunities. They have a strong background and could be a great fit for your team.`;
    }
    
    // Check for skills or expertise to include
    let skillsSection = '';
    if (candidate.skills && candidate.skills.length > 0) {
      const topSkills = candidate.skills.slice(0, 5).join(', ');
      skillsSection = `\nTheir top skills include ${topSkills}.`;
    }
    
    // Create email subject
    const subject = `Introduction: ${candidateName} for ${company.name}`;
    
    // Format the email template with appropriate line breaks
    return `To: ${contactEmail}
Subject: ${subject}

Hi ${contactName},

I hope this email finds you well. My name is ${adminFirstName} from Burnt, and I'm reaching out because we have a candidate who is interested in learning about ${company.name}.

${profileSummary.slice(0, 350)}${profileSummary.length > 350 ? '...' : ''}${skillsSection}

We'd love to connect you directly with ${candidateFirstName} for a conversation about the company and your experience. Would you be open to sharing some of your experience?

If so, let us know a few days and times that work best for you in the next couple of weeks for a brief call.

Thank you,
${adminFirstName}
Burnt
`;
  };

  // Handle opening the contact modal - reset email template
  const handleOpenContactModal = (e, candidate, company, contactLead) => {
    e.stopPropagation(); // Prevent row click
    setSelectedCompanyForContact(company);
    setSelectedCandidate(candidate);
    setSelectedContactLead(contactLead);
    setContactNotes('');
    setSelectedContact(null);
    setShowContactModal(true);
  };

  // Modify handleContactSelect to generate email template when a contact is selected
  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    
    // Generate email template if we have all the necessary info
    if (contact && selectedCandidate && selectedCompanyForContact) {
      const emailTemplate = generateEmailTemplate(contact, selectedCandidate, selectedCompanyForContact);
      setContactNotes(emailTemplate);
    }
  };

  // Handle contact added from the FastTrackContactModal
  const handleContactAdded = (contact) => {
    setShowAddContactModal(false);
    if (contact) {
      setSelectedContact({
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone,
        company: contact.company
      });
      
      // Generate email template for the newly added contact
      if (selectedCandidate && selectedCompanyForContact) {
        const emailTemplate = generateEmailTemplate(contact, selectedCandidate, selectedCompanyForContact);
        setContactNotes(emailTemplate);
      }
    }
  };

  // Handle showing new contact modal
  const handleAddNewContact = () => {
    setShowAddContactModal(true);
  };

  // Handle submit contact
  const handleSubmitContact = async () => {
    if (!selectedCompanyForContact || !selectedCandidate) return;
    
    setIsSubmitting(true);
    try {
      // If there's an assigned admin, pass that info to the contact function
      let adminInfo = null;
      if (selectedContactLead) {
        adminInfo = {
          adminUid: selectedContactLead.uid,
          adminEmail: selectedContactLead.email
        };
      }
      
      // Add contact info to the request
      const contactInfo = selectedContact ? {
        contactId: selectedContact.id,
        contactName: `${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim(),
        contactEmail: selectedContact.email
      } : null;
      
      // Use the email template as the notes field if available
      const notes = contactNotes || (
        contactInfo ? 
        `Contact: ${contactInfo.contactName}${contactInfo.contactEmail ? ` (${contactInfo.contactEmail})` : ''}` :
        ''
      );
      
      const result = await markCompanyContacted(
        selectedCandidate.id, 
        selectedCompanyForContact.id, 
        selectedCompanyForContact.name, 
        notes,
        adminInfo,
        contactInfo
      );
      
      if (result.success) {
        toast.success(`Marked ${selectedCompanyForContact.name} as contacted` + (contactInfo ? ` through ${contactInfo.contactName}` : ''));
        setShowContactModal(false);
        setContactNotes('');
        setSelectedContact(null);
        
        // Trigger refresh
        if (typeof window !== 'undefined') {
          const refreshEvent = new CustomEvent('refresh-candidate-data', { 
            detail: { userId: selectedCandidate.id } 
          });
          window.dispatchEvent(refreshEvent);
        }
      } else {
        toast.error(`Failed to mark company as contacted: ${result.error}`);
      }
    } catch (error) {
      console.error('Error marking company as contacted:', error);
      toast.error('Failed to mark company as contacted');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle opening the tag lead modal
  const handleOpenTagLeadModal = (e, candidate, company) => {
    e.stopPropagation(); // Prevent row click
    setSelectedCompanyForContact(company);
    setSelectedCandidate(candidate);
    setSelectedAdmin('');
    setShowTagLeadModal(true);
  };

  // Handle submit tag lead
  const handleSubmitTagLead = async () => {
    if (!selectedCompanyForContact || !selectedAdmin || !selectedCandidate) {
      toast.error('Please select an admin');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Find the admin object
      const admin = adminOptions.find(a => a.id === selectedAdmin);
      
      // Add a new entry to companyStatusHistory
      const result = await updateCompanyStatusHistory(
        selectedCandidate.id, 
        selectedCompanyForContact.id, 
        'contact_lead_assigned',
        {
          assignedAdminUid: admin.uid,
          assignedAdminEmail: admin.id,
          assignedAdminName: admin.name
        }
      );
      
      if (result.success) {
        toast.success(`Tagged ${admin.name} as contact lead for ${selectedCompanyForContact.name}`);
        setShowTagLeadModal(false);
        
        // Trigger refresh
        if (typeof window !== 'undefined') {
          const refreshEvent = new CustomEvent('refresh-candidate-data', { 
            detail: { userId: selectedCandidate.id } 
          });
          window.dispatchEvent(refreshEvent);
        }
      } else {
        toast.error(`Failed to tag contact lead: ${result.error}`);
      }
    } catch (error) {
      console.error('Error tagging contact lead:', error);
      toast.error('Failed to tag contact lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Component to render a contact lead (with add button if no lead)
  const ContactLeadWithActions = ({ company, candidate, contactLead }) => {
    if (contactLead) {
      // If there's already a contact lead, display it
      return <ContactLeadDisplay contactLead={contactLead} />;
    } else {
      // If no contact lead but company exists in history, show add button
      return (
        <button 
          onClick={(e) => handleOpenTagLeadModal(e, candidate, company)}
          className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors duration-150 font-medium underline"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          add
        </button>
      );
    }
  };

  // Handle sending outreach emails to selected candidates
  const handleSendOutreachEmails = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const sendEmailPromises = selectedIds.map(async (id) => {
        const candidate = candidates.find(c => c.id === id);
        if (!candidate) return { success: false, id, error: 'Candidate not found' };
        
        // Check if this candidate has company connections
        const companies = getAllCompanyInfo(candidate);
        if (companies.length > 0) {
          return { success: false, id, error: 'Candidate already has company connections' };
        }
        
        const candidateName = getSafeDisplayName(candidate);
        const candidateFirstName = candidateName.split(' ')[0];
        
        // Use the Firebase function to send the email
        const functions = getFunctions();
        const sendOutreachEmailFn = httpsCallable(functions, 'sendNoConnectionOutreachEmail');
        
        try {
          const result = await sendOutreachEmailFn({
            email: candidate.email,
            firstName: candidateFirstName
          });
          
          return { success: true, id, messageId: result.data.messageId };
        } catch (error) {
          console.error(`Error sending email to ${candidate.email}:`, error);
          return { success: false, id, error: error.message };
        }
      });
      
      const results = await Promise.all(sendEmailPromises);
      
      // Count successes and failures
      const successes = results.filter(r => r.success).length;
      const failures = results.filter(r => !r.success).length;
      
      if (successes > 0 && failures === 0) {
        toast.success(`Outreach emails sent to ${successes} candidate${successes > 1 ? 's' : ''}`);
      } else if (successes > 0 && failures > 0) {
        toast.success(`Sent ${successes} email${successes > 1 ? 's' : ''}, but ${failures} failed`);
      } else {
        toast.error(`Failed to send emails to ${failures} candidate${failures > 1 ? 's' : ''}`);
      }
      
      setShowOutreachEmailModal(false);
    } catch (error) {
      console.error('Error sending outreach emails:', error);
      toast.error('Failed to send outreach emails');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No candidates found.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      {selectedIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedIds.length} candidate{selectedIds.length > 1 ? 's' : ''} selected
          </div>
          <div className="flex space-x-2">
            {/* Other selection actions could go here */}
          </div>
        </div>
      )}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Candidate Progress</h2>
        <div className="flex space-x-2">
          {selectedIds.length > 0 && (
            <button
              onClick={() => setShowOutreachEmailModal(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Send Personalized Outreach
            </button>
          )}
        </div>
      </div>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div onClick={(e) => e.stopPropagation()}>
                <CustomHeaderCheckbox 
                  isChecked={selectedIds.length === sortedCandidates.length && sortedCandidates.length > 0}
                  isIndeterminate={selectedIds.length > 0 && selectedIds.length < sortedCandidates.length}
                  onChange={handleSelectAll}
                />
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Candidate
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Account Created
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Profile Summary
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Company Connection
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Contact Lead
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Contacted
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedCandidates.map((candidate) => {
            // Get all companies and their contact leads
            const companies = getAllCompanyInfo(candidate);
            const contactLeads = companies.map(company => ({
              company: company,
              lead: findContactLead(candidate, company.id)
            }));
            
            return (
              <tr 
                key={candidate.id} 
                onClick={() => onCandidateClick(candidate.id)}
                className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors duration-150 ${
                  candidate.isArchived ? 'bg-gray-50 dark:bg-gray-800 opacity-75' : ''
                }`}
              >
                {/* Selection Checkbox */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div onClick={(e) => e.stopPropagation()}>
                    <CustomCheckbox 
                      isChecked={selectedIds.includes(candidate.id)}
                      onChange={(e) => handleCheckboxChange(candidate.id, e)}
                    />
                  </div>
                </td>
                
                {/* Candidate Name */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {getSafeDisplayName(candidate)}
                      </div>
                      {candidate.isArchived && (
                        <span className="ml-2 inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-500/10 dark:ring-gray-500/30">
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {candidate.email}
                    </div>
                  </div>
                </td>
                
                {/* Account Created */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {candidate.createdAt ? formatDateOnly(candidate.createdAt) : 'N/A'}
                  </div>
                </td>
                
                {/* Profile Summary */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {candidate.profileSummary && candidate.profileSummary.profileSummary ? (
                      <>
                        <CheckIcon className="h-5 w-5 text-purple-600 mr-1.5" aria-hidden="true" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatTimestamp(
                            candidate.profileSummary.lastEditedByUser || 
                            candidate.profileSummary.generatedAt || 
                            candidate.profileSummary.createdAt
                          )}
                        </span>
                      </>
                    ) : (
                      <div className="flex items-center">
                        <ClockIcon className="h-5 w-5 text-yellow-500 mr-1.5" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">Pending</span>
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Company Connection */}
                <td className="px-6 py-4">
                  {companies.length > 0 ? (
                    <div className="flex flex-col">
                      {companies.map((company, idx) => (
                        <CompanyDisplay key={`${company.id}-${idx}`} company={company} />
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                  )}
                </td>
                
                {/* Contact Lead */}
                <td className="px-6 py-4">
                  {contactLeads.length > 0 ? (
                    <div className="flex flex-col">
                      {contactLeads.map((item, idx) => (
                        <div key={`contact-lead-${item.company.id}-${idx}`} className="mb-2 last:mb-0">
                          <ContactLeadWithActions
                            company={item.company}
                            candidate={candidate}
                            contactLead={item.lead}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                  )}
                </td>
                
                {/* Contacted Status */}
                <td className="px-6 py-4">
                  {companies.length > 0 ? (
                    <div className="flex flex-col">
                      {contactLeads.map((item, idx) => {
                        const isContacted = isCompanyContacted(candidate, item.company.id);
                        
                        return (
                          <div key={`contact-status-${item.company.id}-${idx}`} className="mb-2 last:mb-0 h-7 flex items-center">
                            {isContacted ? (
                              <div className="flex items-center">
                                <div className="h-5 w-5 rounded-full flex items-center justify-center">
                                  <CheckIcon className="h-4 w-4 text-purple-600" />
                                </div>
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                  {formatDateOnly(item.company.timestamp)}
                                </span>
                              </div>
                            ) : item.lead ? (
                              <button 
                                onClick={(e) => handleOpenContactModal(e, candidate, item.company, item.lead)}
                                className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors duration-150 font-medium underline"
                              >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                add
                              </button>
                            ) : (
                              <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Contact Modal */}
      <Transition.Root show={showContactModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowContactModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  {selectedCompanyForContact && selectedCandidate && (
                    <>
                      <div>
                        <div className="absolute right-0 top-0 pr-4 pt-4">
                          <button
                            type="button"
                            className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            onClick={() => setShowContactModal(false)}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                            Mark {selectedCompanyForContact.name} as Contacted
                          </Dialog.Title>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Record that you've reached out to this company on behalf of {getSafeDisplayName(selectedCandidate)}.
                            </p>
                            {selectedContactLead && (
                              <p className="mt-1 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                Contact Lead: {selectedContactLead.name}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Contact selection */}
                        <div className="mt-4">
                          <div className="flex justify-between items-center">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Contact</h4>
                            <button
                              type="button"
                              onClick={handleAddNewContact}
                              className="inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800"
                            >
                              <UserPlusIcon className="h-4 w-4 mr-1" />
                              Add New
                            </button>
                          </div>
                          <div className="mt-2">
                            <ContactSelector
                              onSelectContact={handleContactSelect}
                              userId={selectedCandidate.id}
                              companyId={selectedCompanyForContact.id}
                            />
                          </div>
                          {selectedContact && (
                            <div className="mt-2 p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-md">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {selectedContact.firstName} {selectedContact.lastName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {selectedContact.email} {selectedContact.phone ? ` • ${selectedContact.phone}` : ''}
                              </p>
                              {selectedContact.company && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {selectedContact.company}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-4">
                          <label htmlFor="contact-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Draft Email
                          </label>
                          <div className="mt-1">
                            <textarea
                              id="contact-notes"
                              name="contact-notes"
                              rows={10}
                              className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm font-mono"
                              placeholder="Select a contact to generate a draft email template..."
                              value={contactNotes}
                              onChange={(e) => setContactNotes(e.target.value)}
                            />
                          </div>
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            This email template will be saved with your outreach record. Feel free to edit it before sending.
                          </p>
                        </div>
                      </div>
                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                          onClick={handleSubmitContact}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                          onClick={() => setShowContactModal(false)}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Tag Lead Modal */}
      <Transition.Root show={showTagLeadModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowTagLeadModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  {selectedCompanyForContact && selectedCandidate && (
                    <>
                      <div>
                        <div className="absolute right-0 top-0 pr-4 pt-4">
                          <button
                            type="button"
                            className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            onClick={() => setShowTagLeadModal(false)}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                            Tag Contact Lead for {selectedCompanyForContact.name}
                          </Dialog.Title>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Select an admin who will be responsible for contacting {getSafeDisplayName(selectedCandidate)}.
                            </p>
                          </div>
                        </div>
                        <div className="mt-4">
                          <label htmlFor="admin-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Admin
                          </label>
                          <select
                            id="admin-select"
                            name="admin-select"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
                            value={selectedAdmin}
                            onChange={(e) => setSelectedAdmin(e.target.value)}
                          >
                            <option value="">Select an admin</option>
                            {adminOptions.map((admin) => (
                              <option key={admin.id} value={admin.id}>
                                {admin.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleSubmitTagLead}
                          disabled={isSubmitting || !selectedAdmin}
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                          onClick={() => setShowTagLeadModal(false)}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Outreach Email Modal */}
      <Transition.Root show={showOutreachEmailModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowOutreachEmailModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={() => setShowOutreachEmailModal(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Send Personalized CTO Outreach
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Send a personalized outreach email from Grey (CTO) to {selectedIds.length} selected candidate{selectedIds.length > 1 ? 's' : ''}. This will only send to candidates who haven't connected with a company yet.
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <div className="rounded-md bg-gray-50 dark:bg-gray-700 p-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Preview:</div>
                        <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono">
                          {`Hey [Candidate First Name], 

Welcome to Burnt. We are excited to have you on the platform!

My name is Grey Weissend, I'm the CTO and co-founder. I saw you're haven't selected a company to connected with yet. The process takes 2 minutes and is 100% free.

I'd love to learn more about what you were looking for (if it wasn't there) and how we could improve the process so you get connected to a great company. Happy to chat at your convenience - or just reply here. 

As a start-up laser focused on helping you - we'd really appreciate your feedback!

Thanks,
Grey

CTO and Co-Founder
https://www.justburnt.com/

Book a 15-minute chat: https://cal.com/grey-w/15min`}
                        </pre>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleSendOutreachEmails}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Emails'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                      onClick={() => setShowOutreachEmailModal(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <FastTrackContactModal
          companyId={selectedCompanyForContact?.id}
          onClose={() => setShowAddContactModal(false)}
          onContactAdded={handleContactAdded}
        />
      )}
    </div>
  );
} 